import styled from "styled-components"

import {
  cardBoxShadow,
  colors,
  getPortalRoot,
  Popover,
  PopoverProps,
  Text,
} from "@minuthq/meatball-ui-react"

import { MButton, TMButtonProps } from "src/ui/Button/MButton"
import ExpandDownIcon from "src/ui/icons/expand-down.svg"
import { spacing } from "src/ui/spacing"

export type DropdownButtonBaseProps = Omit<TMButtonProps, "children"> & {
  label?: string
  displayValue?: React.ReactNode
  placement?: PopoverProps["placement"]
  children?: (args: { closeMenu: () => void }) => React.ReactNode
}

function DropdownButtonBase({
  label,
  displayValue,
  placement = "bottom",
  variant = "secondary",
  size = "small",
  onClick,
  children,
  color,
  disabled,
  fullWidth,
}: DropdownButtonBaseProps) {
  function renderTrigger() {
    return (
      <MButton
        variant={variant}
        color={color}
        size={size}
        onClick={onClick}
        endIcon={<ExpandDownIcon fill="currentColor" width="10" />}
        disabled={disabled}
        fullWidth={fullWidth}
      >
        {displayValue}
      </MButton>
    )
  }

  if (!children) {
    return renderTrigger()
  }

  return (
    <Box>
      {label && (
        <Text variant="subtitleS" marginBottom={spacing.XS2}>
          {label}
        </Text>
      )}

      <Popover
        trigger={renderTrigger()}
        placement={placement}
        offset={10}
        root={getPortalRoot() ?? undefined}
      >
        {({ closeMenu }) => (
          <DropdownContentBox>
            {children({
              closeMenu: () => {
                closeMenu()
              },
            })}
          </DropdownContentBox>
        )}
      </Popover>
    </Box>
  )
}

const Box = styled.div``

const DropdownContentBox = styled.div`
  background-color: ${colors.neutralLight};
  padding-block: ${spacing.XS};
  border-radius: 0.5rem;
  border: 1px solid ${colors.neutralDark};
  ${cardBoxShadow}
  min-width: 200px;
`

export function DropdownButton({
  variant = "secondary",
  size = "small",
  ...props
}: DropdownButtonBaseProps) {
  return <DropdownButtonBase variant={variant} size={size} {...props} />
}

// ts-prune-ignore-next
export function DropdownSelect(props: DropdownButtonBaseProps) {
  return <DropdownButtonBase variant="secondary" size="small" {...props} />
}
